import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { CustomTheme } from "../../../../types/customTheme";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 10,
    },
    header: {
      paddingBottom: theme.spacing(1),
    },
    searchField: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.background.entity_background,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    listItem: {
      borderRadius: 10,
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.background.entity_background,
      border: `1px solid ${theme.palette.divider}`,
      transition: "transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        transform: "translateY(-1px)",
        boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
      },
    },
    divider: {
      display: "none",
    },
    emptyState: {
      padding: theme.spacing(2),
      borderRadius: 10,
      backgroundColor: theme.palette.background.entity_background,
      border: `1px dashed ${theme.palette.divider}`,
    },
    skeletonItem: {
      borderRadius: 10,
      marginBottom: theme.spacing(1),
      padding: theme.spacing(1.5),
      backgroundColor: theme.palette.background.entity_background,
      border: `1px solid ${theme.palette.divider}`,
    },
  }),
);

interface ChargebeeCustomer {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  customers: ChargebeeCustomer[];
  loading: boolean;
  onSelect: (customer: ChargebeeCustomer) => void;
}

const ChargebeeCustomerModal: React.FC<Props> = ({
  open,
  onClose,
  customers,
  loading,
  onSelect,
}) => {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<ChargebeeCustomer | null>(null);

  const getCustomerName = (customer: ChargebeeCustomer | null) => {
    if (!customer) return "";

    return (
      customer.company ||
      `${customer.first_name || ""} ${customer.last_name || ""}`.trim() ||
      customer.id
    );
  };

  const handleCustomerClick = (customer: ChargebeeCustomer) => {
    setSelectedCustomer(customer);
  };

  const handleConfirmCustomer = () => {
    if (!selectedCustomer) return;

    onSelect(selectedCustomer);
    setSelectedCustomer(null);
  };

  const handleCancelConfirm = () => {
    setSelectedCustomer(null);
  };

  const renderCustomerSkeleton = () => (
    <>
      <Box mb={2}>
        <Skeleton variant="rect" height={56} style={{ borderRadius: 10 }} />
      </Box>

      <List>
        {[...Array(5)].map((_, index) => (
          <Box key={index} className={classes.skeletonItem}>
            <Skeleton
              variant="text"
              width="42%"
              height={24}
              style={{ marginBottom: 6 }}
            />
            <Skeleton variant="text" width="72%" height={20} />
          </Box>
        ))}
      </List>
    </>
  );

  const filteredCustomers = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return customers;

    return customers.filter((customer) => {
      const text = [
        customer.id,
        customer.first_name,
        customer.last_name,
        customer.email,
        customer.company,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return text.includes(keyword);
    });
  }, [customers, search]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>
          <Box className={classes.header}>
            <Typography variant="overline" color="textSecondary">
              Chargebee
            </Typography>
            <Typography variant="h6">Select Chargebee Customer</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {loading ? (
            renderCustomerSkeleton()
          ) : (
            <>
              <Box mb={2}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Search customers"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={classes.searchField}
                />
              </Box>

              {filteredCustomers.length === 0 ? (
                <Box className={classes.emptyState}>
                  <Typography>No customers found.</Typography>
                </Box>
              ) : (
                <List>
                  {filteredCustomers.map((customer) => (
                    <React.Fragment key={customer.id}>
                      <ListItem
                        button
                        onClick={() => handleCustomerClick(customer)}
                        className={classes.listItem}
                      >
                        <ListItemText
                          primary={
                            customer.company ||
                            `${customer.first_name || ""} ${
                              customer.last_name || ""
                            }`.trim() ||
                            customer.id
                          }
                          secondary={
                            <>
                              ID: {customer.id}
                              {customer.email ? ` | Email: ${customer.email}` : ""}
                            </>
                          }
                        />
                      </ListItem>
                      <Divider className={classes.divider} />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <ButtonCommon
            variant="contained"
            color="orange"
            onClick={onClose}
            style={{ minWidth: 120, height: 36 }}
          >
            Cancel
          </ButtonCommon>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedCustomer)} onClose={handleCancelConfirm}>
        <DialogTitle>Confirm Chargebee Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to add {getCustomerName(selectedCustomer)} as a
            Chargebee customer?
          </Typography>
        </DialogContent>
        <DialogActions>
          <ButtonCommon
            variant="contained"
            color="orange"
            onClick={handleCancelConfirm}
            style={{ minWidth: 120, height: 36, fontSize: 13, fontWeight: 600 }}
          >
            Cancel
          </ButtonCommon>
          <ButtonCommon
            variant="contained"
            color="green"
            onClick={handleConfirmCustomer}
            style={{ minWidth: 120, height: 36, fontSize: 13, fontWeight: 600 }}
          >
            Confirm
          </ButtonCommon>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChargebeeCustomerModal;
